import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { brl } from "../lib/format";
import { CurrencyDollar, VideoCamera, Package, UsersFour, ShoppingCart, ArrowRight } from "@phosphor-icons/react";
import PeriodFilter, { toQuery } from "../components/PeriodFilter";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
  PieChart, Pie, Cell, ReferenceLine,
} from "recharts";

const METHOD_OPTIONS = [
  { key: "", label: "Todos" },
  { key: "pix", label: "Pix" },
  { key: "credit", label: "Cartão" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [lives, setLives] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [period, setPeriod] = useState({ from: "", to: "" });
  const [method, setMethod] = useState("");
  const [monthlyGoal, setMonthlyGoal] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const qs = toQuery(period);
    const sep = qs ? "&" : "?";
    const methodQs = method ? `${sep}payment_method=${method}` : "";
    const [s, l, m, st] = await Promise.all([
      api.get(`/dashboard${qs}${methodQs}`),
      api.get(`/lives${qs}`),
      api.get(`/dashboard/monthly-methods${qs}`),
      api.get(`/settings`).catch(() => ({ data: null })),
    ]);
    setStats(s.data);
    setLives(l.data);
    setMonthly(m.data);
    const g = st.data?.monthly_goal;
    setMonthlyGoal(g != null && !isNaN(parseFloat(g)) ? parseFloat(g) : null);
  }, [period, method]);

  useEffect(() => { load(); }, [load]);

  const cards = [
    { label: "Total vendido", value: brl(stats?.total || 0), color: "bg-brand-mint", icon: CurrencyDollar, testid: "stat-total" },
    { label: "Recebido", value: brl(stats?.paid || 0), color: "bg-brand-yellow", icon: CurrencyDollar, testid: "stat-paid" },
    { label: "A receber", value: brl(stats?.pending || 0), color: "bg-brand-peach", icon: CurrencyDollar, testid: "stat-pending" },
    { label: "Pedidos", value: stats?.orders_count || 0, color: "bg-brand-purple", icon: ShoppingCart, testid: "stat-orders" },
  ];

  const shareCards = [
    { label: "Faturamento Desapegue", value: brl(stats?.shop_revenue || 0), color: "bg-brand-pink text-white", testid: "stat-shop-revenue" },
    { label: "Repasse aos donos", value: brl(stats?.contractor_payout || 0), color: "bg-brand-cream", testid: "stat-contractor-payout" },
    { label: "Desistências", value: brl(stats?.withdrawn || 0), color: "bg-brand-peach", testid: "stat-withdrawn" },
    { label: "Desapegue · Pix", value: brl(stats?.shop_revenue_pix || 0), color: "bg-brand-mint", testid: "stat-shop-pix" },
    { label: "Desapegue · Cartão", value: brl(stats?.shop_revenue_credit || 0), color: "bg-brand-yellow", testid: "stat-shop-credit" },
  ];

  const methodCards = [
    { label: "Recebido · Pix", value: brl(stats?.paid_pix || 0), color: "bg-brand-mint", testid: "stat-paid-pix" },
    { label: "Recebido · Cartão", value: brl(stats?.paid_credit || 0), color: "bg-brand-yellow", testid: "stat-paid-credit" },
    { label: "Recebido · Outros", value: brl(stats?.paid_other || 0), color: "bg-brand-cream", testid: "stat-paid-other" },
  ];

  const secondary = [
    { label: "Lives", value: stats?.lives_count || 0, icon: VideoCamera, to: "/lives", testid: "quick-lives" },
    { label: "Produtos", value: stats?.products_count || 0, icon: Package, to: "/products", testid: "quick-products" },
    { label: "Clientes", value: stats?.customers_count || 0, icon: UsersFour, to: "/customers", testid: "quick-customers" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <header>
        <p className="label-brutal">Visão geral</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black">Bora vender ao vivo?</h1>
        <p className="text-brand-ink2 mt-2">Resumo do seu ateliê digital, tudo num só lugar.</p>
      </header>

      <PeriodFilter value={period} onChange={setPeriod} testid="dash-period" />

      <div className="flex flex-wrap gap-2 items-center">
        <span className="label-brutal !mb-0">Método:</span>
        {METHOD_OPTIONS.map((o) => (
          <button
            key={o.key || "all"}
            data-testid={`dash-method-${o.key || "all"}`}
            onClick={() => setMethod(o.key)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 border-2 border-brand-ink text-xs font-bold uppercase ${
              method === o.key ? "bg-brand-pink text-white" : "bg-white hover:bg-brand-yellow"
            }`}
          >
            {o.label}
          </button>
        ))}
        {method && (
          <span className="text-xs text-brand-ink2">Total e Pedidos filtrados por <strong>{method === "pix" ? "Pix" : "Cartão"}</strong></span>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className={`card-brutal p-5 ${c.color}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold uppercase tracking-widest">{c.label}</p>
              <c.icon weight="bold" size={18} />
            </div>
            <p className="font-heading text-2xl sm:text-3xl font-black">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Breakdown por forma de pagamento — sempre visível, independente do filtro acima */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {methodCards.map((c) => (
          <div key={c.label} data-testid={c.testid} className={`card-brutal p-4 ${c.color}`}>
            <p className="text-[11px] font-bold uppercase tracking-widest">{c.label}</p>
            <p className="font-heading text-xl sm:text-2xl font-black mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Split Desapegue vs Repasse (por live.commission_pct) */}
      <section>
        <div className="mb-2">
          <p className="label-brutal">Split por live</p>
          <h3 className="font-heading text-xl font-black">Faturamento Desapegue vs Repasse aos donos</h3>
          <p className="text-xs text-brand-ink2">Calculado aplicando o % informado em cada live sobre o valor recebido. Lives sem % configurado contam 100% para o Desapegue.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {shareCards.map((c) => (
            <div key={c.label} data-testid={c.testid} className={`card-brutal p-4 ${c.color}`}>
              <p className="text-[11px] font-bold uppercase tracking-widest">{c.label}</p>
              <p className="font-heading text-xl sm:text-2xl font-black mt-1">{c.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Charts: Donut Pix vs Cartão + Monthly comparison */}
      <MethodCharts stats={stats} monthly={monthly} monthlyGoal={monthlyGoal} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {secondary.map((c) => (
          <button
            key={c.label}
            data-testid={c.testid}
            onClick={() => nav(c.to)}
            className="card-brutal p-5 text-left card-brutal-hover"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-brand-ink2">{c.label}</p>
                <p className="font-heading text-3xl font-black mt-1">{c.value}</p>
              </div>
              <div className="w-10 h-10 bg-brand-pink border-2 border-brand-ink flex items-center justify-center">
                <c.icon weight="bold" size={20} color="#F8F0D8" />
              </div>
            </div>
          </button>
        ))}
      </div>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-black">Últimas lives</h2>
          <button data-testid="see-all-lives" onClick={() => nav("/lives")} className="btn-white !px-3 !py-2">
            Ver todas <ArrowRight weight="bold" size={16} />
          </button>
        </div>

        {lives.length === 0 ? (
          <div className="card-brutal p-8 text-center">
            <p className="font-bold">Nenhuma live cadastrada.</p>
            <p className="text-brand-ink2 text-sm mt-1">Comece agora mesmo cadastrando sua primeira live.</p>
            <button data-testid="empty-create-live" onClick={() => nav("/lives")} className="btn-pink mt-4">
              Criar live
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lives.slice(0, 6).map((lv) => (
              <button
                key={lv.id}
                data-testid={`dash-live-${lv.id}`}
                onClick={() => nav(`/lives/${lv.id}`)}
                className="card-brutal card-brutal-hover p-5 text-left"
              >
                <p className="text-[11px] font-bold uppercase tracking-widest text-brand-ink2">{lv.date}</p>
                <p className="font-heading text-xl font-black mt-1">{lv.name}</p>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  <span className="badge-brutal bg-brand-mint">{lv.orders_count} pedidos</span>
                  <span className="badge-brutal bg-brand-yellow">{brl(lv.total)}</span>
                  {lv.pending > 0 ? (
                    <span className="badge-brutal bg-brand-peach">{brl(lv.pending)} a receber</span>
                  ) : (
                    <span className="badge-brutal bg-brand-success text-white">tudo pago</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const MONTH_LABEL = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const fmtMonth = (ym) => {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTH_LABEL[idx] || m}/${y.slice(2)}`;
};
const fmtBrlShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(1).replace(".", ",")}k`;
  return `R$ ${n.toFixed(0)}`;
};

function MethodCharts({ stats, monthly, monthlyGoal }) {
  const donutData = [
    { name: "Pix", value: stats?.paid_pix || 0, color: "#0EA57D" },
    { name: "Cartão", value: stats?.paid_credit || 0, color: "#F5C400" },
    { name: "Outros", value: stats?.paid_other || 0, color: "#B9B0A0" },
  ].filter(d => d.value > 0);
  const totalDonut = donutData.reduce((s, d) => s + d.value, 0);
  const monthlyData = (monthly || []).map(m => ({ ...m, label: fmtMonth(m.month) }));
  const currentMonthKey = new Date().toISOString().slice(0, 7);
  const currentMonth = monthlyData.find(m => m.month === currentMonthKey);
  const currentShop = currentMonth?.shop || 0;
  const hasGoal = typeof monthlyGoal === "number" && monthlyGoal > 0;
  const remaining = hasGoal ? Math.max(0, monthlyGoal - currentShop) : 0;
  const progress = hasGoal ? Math.min(100, (currentShop / monthlyGoal) * 100) : 0;

  if (!totalDonut && monthlyData.length === 0) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4">
      <div data-testid="dashboard-donut" className="card-brutal p-4">
        <p className="label-brutal">Mix por método</p>
        <h3 className="font-heading text-xl font-black">Pix vs Cartão</h3>
        {totalDonut > 0 ? (
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} strokeWidth={2} stroke="#1A1A1A">
                  {donutData.map((d) => (
                    <Cell key={d.name} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => brl(v)} />
                <Legend verticalAlign="bottom" iconType="square" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-brand-ink2 text-sm mt-2">Nada recebido no período.</p>
        )}
      </div>

      <div data-testid="dashboard-monthly-methods" className="card-brutal p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="label-brutal">Evolução mensal</p>
            <h3 className="font-heading text-xl font-black">Receita por mês · Pix, Cartão e Meta Desapegue</h3>
          </div>
          {hasGoal && (
            <div data-testid="monthly-goal-callout" className="text-xs card-brutal !p-2 bg-brand-pink text-white min-w-[220px]">
              <p className="font-bold uppercase tracking-wider text-[10px]">Meta do mês</p>
              <p className="font-heading text-base font-black">{brl(monthlyGoal)}</p>
              <div className="mt-1 h-2 bg-white/30 border border-brand-ink overflow-hidden">
                <div className="h-full bg-white" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-1">
                {remaining > 0
                  ? <>Faltam <strong>{brl(remaining)}</strong> · {progress.toFixed(0)}%</>
                  : <>🎉 Meta batida! (+{brl(currentShop - monthlyGoal)})</>}
              </p>
            </div>
          )}
        </div>
        {monthlyData.length > 0 ? (
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DFCF" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fontWeight: 700 }} />
                <YAxis tickFormatter={fmtBrlShort} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => brl(v)} />
                <Legend iconType="square" />
                <Bar dataKey="pix" name="Pix" stackId="a" fill="#0EA57D" stroke="#1A1A1A" strokeWidth={1} />
                <Bar dataKey="credit" name="Cartão" stackId="a" fill="#F5C400" stroke="#1A1A1A" strokeWidth={1} />
                <Bar dataKey="other" name="Outros" stackId="a" fill="#B9B0A0" stroke="#1A1A1A" strokeWidth={1} />
                {hasGoal && (
                  <ReferenceLine
                    y={monthlyGoal}
                    stroke="#E5006E"
                    strokeWidth={2.5}
                    strokeDasharray="6 4"
                    ifOverflow="extendDomain"
                    label={{ value: `Meta ${brl(monthlyGoal)}`, position: "insideTopRight", fill: "#E5006E", fontSize: 11, fontWeight: 700 }}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-brand-ink2 text-sm mt-2">Sem histórico de pagamentos ainda.</p>
        )}
      </div>
    </div>
  );
}
